import { useState } from "react";
import { useRelationshipLevels, useUpdateRelationshipLevel } from "@/hooks/queries";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import styles from "./styles.module.css";

type Props = {
  profileId: string;
  currentLevel: number | null | undefined;
  onLevelChanged?: () => void;
};

export const RelationshipLevelSelect = ({
  profileId,
  currentLevel,
  onLevelChanged,
}: Props) => {
  const { data: levels = [] } = useRelationshipLevels();
  const updateLevel = useUpdateRelationshipLevel();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const currentLevelData = levels.find((l) => l.level === currentLevel);

  const handleSelect = async (newLevel: number | null) => {
    setAnchorEl(null);
    if (newLevel === currentLevel) return;
    updateLevel.mutate(
      { profileId, level: newLevel },
      { onSuccess: () => onLevelChanged?.() }
    );
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={updateLevel.isPending}
        className={styles.triggerButton}
      >
        {currentLevelData
          ? `${currentLevelData.level} - ${currentLevelData.title}`
          : <span className={styles.placeholder}>Set level…</span>}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <table className={styles.levelTable}>
          <thead>
            <tr>
              <th>Level</th>
              <th>Title</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr
              className={`${styles.levelRow} ${currentLevel == null ? styles.selected : ""}`}
              onClick={() => handleSelect(null)}
            >
              <td colSpan={3}><em>None</em></td>
            </tr>
            {levels.map((level) => (
              <tr
                key={level.level}
                className={`${styles.levelRow} ${currentLevel === level.level ? styles.selected : ""}`}
                onClick={() => handleSelect(level.level)}
              >
                <td className={styles.levelNum}>{level.level}</td>
                <td className={styles.levelTitle}>{level.title}</td>
                <td className={styles.levelDesc}>{level.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Popover>
    </>
  );
};
